import { Button, Text } from '@simplywallst/ui-core';
import { FormContainer } from './styled';
import If from '@components/If';
import { useSelector } from 'react-redux';
import { ROUTE_COMPANY } from '@/constants/routes';
import { getLocation } from '@/redux/location/selectors';
import loadable from '@loadable/component';
import { useFlag } from '@/features/unleash';
import type { Method } from '@/components/AuthenticationSelect';

const RegistrationForm = loadable(
  () => import('@/components/AuthenticationSelect/components/RegistrationForm')
);

const LoginForm = loadable(
  () => import('@/components/AuthenticationSelect/components/LoginForm')
);

const ContinueWithButtons = loadable(
  () => import('./components/ContinueWithButtons')
);
const ContinueWithButtonsInlineExperiment = loadable(
  () => import('./components/ContinueWithButtonsInlineExperiment')
);

const OAuthIconButtons = loadable(
  () => import('@/components/AuthenticationSelect/components/OAuthButtons'),
  {
    resolveComponent: (component) => component.OAuthButtons,
  }
);

interface Props {
  current?: Method;
  onChangeType?: (type: Method) => void;
  onRegisterSuccess?: () => void;
  onLoginSuccess?: () => void;
  ticker?: string;
}

export const AuthenticationSelectExperiments = ({
  onRegisterSuccess = () => null,
  onLoginSuccess = () => null,
  onChangeType,
  current,
  ticker,
}: Props) => {
  const location = useSelector(getLocation);

  const flag = useFlag({ name: 'mono-inline-rego' });

  const shouldShowExperiments =
    flag.enabled &&
    flag.flagsReady &&
    flag.variant.name === 'default-inline-rego';
  const isCompanyPage = location.type === ROUTE_COMPANY;

  return (
    <>
      <If
        conditions={{
          select: current === 'oauth',
          loginRegister: current !== 'oauth',
        }}
      >
        {{
          select: () =>
            shouldShowExperiments && isCompanyPage ? (
              <ContinueWithButtonsInlineExperiment
                onLoginSuccess={onLoginSuccess}
                onRegisterSuccess={onRegisterSuccess}
                onClickRegister={() => {
                  if (typeof onChangeType === 'function') {
                    onChangeType('register');
                  }
                }}
                onClickSignIn={() => {
                  if (typeof onChangeType === 'function') {
                    onChangeType('login');
                  }
                }}
                ticker={ticker}
              />
            ) : (
              <ContinueWithButtons
                onLoginSuccess={onLoginSuccess}
                onRegisterSuccess={onRegisterSuccess}
                onClickRegister={() => {
                  if (typeof onChangeType === 'function') {
                    onChangeType('register');
                  }
                }}
                onClickSignIn={() => {
                  if (typeof onChangeType === 'function') {
                    onChangeType('login');
                  }
                }}
                ticker={ticker}
              />
            ),
          loginRegister: () => (
            <>
              <FormContainer>
                <If
                  conditions={{
                    signIn: current === 'login',
                    register: current === 'register',
                  }}
                >
                  {{
                    signIn: () => <LoginForm onSuccess={onLoginSuccess} />,
                    register: () => (
                      <RegistrationForm onRegister={onRegisterSuccess} />
                    ),
                  }}
                </If>
              </FormContainer>
              {current === 'login' || current === 'register' ? (
                <OAuthIconButtons
                  type={current}
                  onLoginSuccess={onLoginSuccess}
                  onRegisterSuccess={onRegisterSuccess}
                />
              ) : null}
              {current === 'login' && (
                <Text
                  textAlign="center"
                  color={'darkGray'}
                  typography={'small'}
                  style={{ paddingBottom: '1em' }}
                >
                  <span>Not a member? </span>
                  <Button
                    styleType="strongLink"
                    onClick={() => {
                      onChangeType && onChangeType('register');
                    }}
                    data-cy-id="toggle-register"
                  >
                    Sign up
                  </Button>
                </Text>
              )}
              {current === 'register' && (
                <Text
                  textAlign="center"
                  color={'darkGray'}
                  typography={'small'}
                  style={{ paddingBottom: '1em' }}
                >
                  <span>Already have an account? </span>
                  <Button
                    styleType="strongLink"
                    onClick={() => onChangeType && onChangeType('login')}
                    data-cy-id="toggle-login"
                  >
                    Sign in
                  </Button>
                </Text>
              )}
            </>
          ),
        }}
      </If>
    </>
  );
};
